type FieldLabelProps = {
  topLabel?: string
  topDescription?: string
  topLabelIcon?: string
  required?: boolean
  /**
   * Id of the input this labels. When set the label renders as a real
   * `<label for>` — screen readers announce it and tapping it focuses the
   * field. Selects/date pickers that don't expose an input id omit it and
   * keep the plain text node.
   */
  htmlFor?: string
}

/**
 * The label + hint above every Base* input (text, select, multi-select, date).
 * Renders on public lead forms, linear flows and dashboards alike, so it stays
 * in the shared Rubik/ink vocabulary: primary label `ink-700`, hint `ink-200`
 * (≥ 5.2:1 on white and on `cream-300` — the old `black-400/80` hint was
 * ~4.3:1). Metrics (15/20 label, 13px hint, `mb-2` rhythm) are unchanged so no
 * form reflows.
 */
export default function FieldLabel({
  topLabel,
  topDescription,
  topLabelIcon,
  required,
  htmlFor
}: FieldLabelProps) {
  if (!topLabel) return null
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        {topLabelIcon && (
          <i
            aria-hidden="true"
            className={`si ${topLabelIcon} size-4 text-[16px] leading-none text-ink-200`}
          />
        )}
        {/* One text node: `{topLabel} {required && '*'}` was three, and this
            renders on public SSR lead forms (Trigger H / #418). */}
        {htmlFor ? (
          <label
            htmlFor={htmlFor}
            className="text-[15px] leading-5 font-medium text-ink-700"
          >
            {required ? `${topLabel} *` : topLabel}
          </label>
        ) : (
          <p className="text-[15px] leading-5 font-medium text-ink-700">
            {required ? `${topLabel} *` : topLabel}
          </p>
        )}
      </div>
      {topDescription && (
        <p className="mb-2 text-[13px] text-ink-200">{topDescription}</p>
      )}
    </div>
  )
}
